import type {
	JiraSideNav,
	JiraSideNavMessageMetadata,
} from '@post-office/placement-create-pipeline/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-create-pipeline/create-pipeline';

import { type MessageTemplateContext } from '../../..';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { type AnyHydrationResult } from '@post-office/shared-contracts/placement-pipe/pipeline-types';
import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import { type JSX } from 'react/jsx-runtime';
import type {
	HydrationResult as HydrationResultShape,
	AdditionalProps,
} from '@post-office/message-templates-common--puma/in-app/cjn-nav4/types';
import { type GenerateValidationFor } from '@atlassian/vindicated';

export type HydrationResult = HydrationResultShape;

export type VHydrationResult = GenerateValidationFor<HydrationResult>;

export type RenderProps = HydrationResult & AdditionalProps;

type Pipe = JiraSideNav<{
	messageContext: MessageTemplateContext;
	hydrationResult: HydrationResult;
}>;

const clientPipeline = createClientPipeline<Pipe>();
export const createRender: <U extends (renderProps: RenderProps) => JSX.Element | null>(
	render: U,
) => (renderProps: RenderProps) => JSX.Element | null = clientPipeline.createRender;
export const createValidate: (
	validate: (apiResponse: unknown) => HydrationResult,
) => (apiResponse: unknown) => HydrationResult = clientPipeline.createValidate;
const serverPipeline = createServerPipeline<Pipe>();
export const createHydrate: (
	hydrate: (
		requestContext: RequestContext,
		placementContext: Record<string, never>,
		messageContext: MessageTemplateContext,
	) => Promise<HydrationResult>,
) => (
	requestContext: RequestContext,
	placementContext: Record<string, never>,
	messageContext: MessageTemplateContext & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Promise<AnyHydrationResult> = serverPipeline.createHydrate;
export const createHydrateExample: (
	hydrateExample: (
		requestContext: Partial<RequestContext>,
		placementContext: Record<string, never>,
		messageContext: MessageTemplateContext,
	) => HydrationResult,
	config: {
		requestContext?: Partial<RequestContext>;
		additionalRenderProps: Omit<RenderProps, keyof HydrationResult>;
	},
) => (
	requestContext: Partial<RequestContext>,
	placementContext: Record<string, never>,
	messageContext: MessageTemplateContext & {
		messageInstanceId: string;
		eventTime: string;
	},
) => {
	onDismissed: () => void;
} = serverPipeline.createHydrateExample;
export const createTransformMetadata: (
	transformMetadata: (params: {
		messageContext: MessageTemplateContext;
	}) => JiraSideNavMessageMetadata,
) => (params: { messageContext: MessageTemplateContext }) => TransformMetadataResult =
	serverPipeline.createTransformMetadata;
